import { CaretLeftIcon } from "@phosphor-icons/react";
import { type CredentialResponse, GoogleLogin } from "@react-oauth/google";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { HandraiseLogoMark } from "@/assets/handraise";
import { AuthForm } from "@/components/auth/AuthForm";
import BackgroundGradientLayout from "@/components/common/Gradient";
import { Button } from "@/components/ui/button";
import { resolveAuthenticatedLandingHref } from "@/lib/auth/authLoginUtils";
import {
  type LoginData,
  loginSchema,
  type SsoLoginData,
  ssoLoginSchema,
} from "@/lib/auth/authValidation";
import { useFeature } from "@/lib/feature-management";
import { cn } from "@/lib/utils";
import {
  handleSsoReturnFromUrl,
  startGoogleLogin,
  startLogin,
  startSso,
} from "@/store/auth.async-actions";
import useAuth from "@/store/auth.slice";

const LoginRoute = () => {
  const navigate = useNavigate();
  const [isSsoMode, setIsSsoMode] = useState(false);
  const authStatus = useAuth((state) => state.status);
  const loggedIn = useAuth((state) => state.loggedIn);
  const lastVisitedLocation = useAuth(
    (state) => state.user.last_visited_location,
  );
  const enableSsoLogin = useFeature("enable-sso-login", false);
  const isAuthLoading = authStatus === "loading";

  const handleGoogleLogin = (response: CredentialResponse) => {
    if (response.credential) {
      startGoogleLogin({
        client_id: response.clientId || "",
        access_token: response.credential,
      });
    }
  };

  const handleLogin = async (data: LoginData) => {
    await startLogin({
      email: data.email,
      password: data.password,
    });
  };

  const handleSsoLogin = async (data: SsoLoginData) => {
    await startSso(data.email);
  };

  // Runs once: the handler strips the SSO params before it awaits, so a
  // StrictMode remount finds nothing to exchange and single-use codes are safe.
  useEffect(() => {
    handleSsoReturnFromUrl();
  }, []);

  useEffect(() => {
    if (loggedIn && authStatus === "succeeded") {
      navigate({
        href: resolveAuthenticatedLandingHref(lastVisitedLocation),
        replace: true,
      });
    }
  }, [loggedIn, authStatus, lastVisitedLocation, navigate]);

  return (
    <BackgroundGradientLayout>
      <div className="flex justify-center items-center h-screen">
        <div
          className={cn(
            "w-full max-w-md px-8 pt-6 pb-5 mb-28",
            "bg-white/75 backdrop-blur-xs shadow-xl rounded-lg",
            "border border-gray-100/30",
          )}
        >
          <div className="w-full flex items-center justify-center mt-3 mb-6">
            <HandraiseLogoMark className="w-48" />
          </div>

          <h1 className="text-center text-lg font-serif text-gray-700">
            Welcome back
          </h1>
          <p className="text-center text-gray-500 text-sm mb-6">
            Supercharge your news with AI
          </p>

          {isSsoMode ? (
            <>
              <Button
                variant="ghost"
                isDisabled={isAuthLoading}
                onPress={() => setIsSsoMode(false)}
                className="mb-2 -ml-2 flex items-center text-gray-600"
              >
                <CaretLeftIcon className="mr-1" />
                Back
              </Button>

              <AuthForm
                schema={ssoLoginSchema}
                onSubmit={handleSsoLogin}
                isDisabled={isAuthLoading}
                fields={[
                  {
                    name: "email",
                    label: "Email",
                    type: "email",
                  },
                ]}
                submitLabel="Continue with SSO"
              />
            </>
          ) : (
            <>
              <div
                className={cn(
                  "mb-6 w-full flex justify-center",
                  isAuthLoading && "pointer-events-none opacity-60",
                )}
              >
                <div className="inline-flex w-fit flex-col items-stretch gap-3">
                  <GoogleLogin
                    onSuccess={handleGoogleLogin}
                    theme="outline"
                    text="signin_with"
                  />
                  {enableSsoLogin && (
                    <Button
                      variant="outline"
                      className="h-10 w-full text-xs rounded-sm"
                      isDisabled={isAuthLoading}
                      onPress={() => setIsSsoMode(true)}
                    >
                      Sign in with SSO
                    </Button>
                  )}
                </div>
              </div>

              <div
                className={cn(
                  isAuthLoading && "pointer-events-none opacity-60",
                )}
              >
                <div className="relative mb-6 flex items-center justify-center">
                  <div className="grow border-t border-gray-300" />
                  <span className="px-4 text-gray-500">or</span>
                  <div className="grow border-t border-gray-300" />
                </div>

                <AuthForm
                  schema={loginSchema}
                  onSubmit={handleLogin}
                  isDisabled={isAuthLoading}
                  fields={[
                    {
                      name: "email",
                      label: "Email",
                      type: "email",
                    },
                    {
                      name: "password",
                      label: "Password",
                      type: "password",
                    },
                  ]}
                  submitLabel="Sign In"
                />

                <div className="mt-4 text-center">
                  <Link
                    to="/auth/forgot-password"
                    className="text-gray-700 text-sm hover:underline"
                    tabIndex={isAuthLoading ? -1 : undefined}
                    aria-disabled={isAuthLoading}
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>
            </>
          )}

          <div className="mt-10 text-center text-xs text-gray-500">
            By signing in, you agree to our privacy policy
          </div>
        </div>
      </div>
    </BackgroundGradientLayout>
  );
};

export default LoginRoute;
